import { IssueSourceDtoEntityTypeEnum } from '@issuemanager/api-client';
import { ExtendedControlledAutocomplete } from '@issuemanager/apps/client/components';
import {
  DEBOUNCE_TIME,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useInfiniteObjects } from '@issuemanager/client/features/issues';
import { AutocompleteInputChangeReason } from '@mui/material';
import { debounce } from 'lodash';
import { ReactNode, useCallback, useState } from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';

interface ObjectSelectorProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>;
  label: string;
  selectedType: IssueSourceDtoEntityTypeEnum;
  control: Control<TFieldValues>;
  rules?: object;
}

function ObjectSelector<TFieldValues extends FieldValues>({
  name,
  label,
  selectedType,
  control,
  rules,
}: ObjectSelectorProps<TFieldValues>): ReactNode {
  const [filterByText, setFilterByText] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleTextChange = useCallback(
    debounce((value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input') {
        setFilterByText(truncateSearchText(value));
      }
    }, DEBOUNCE_TIME),
    [],
  );

  const currentObjects = useInfiniteObjects({
    filter: {
      searchTerm: filterByText,
      type: selectedType,
    },
    handleError: true,
  });

  const options = [
    ...(currentObjects.data?.pages.map((c) => c.data).flat() ?? []),
  ];

  return (
    <ExtendedControlledAutocomplete
      name={name}
      control={control}
      rules={rules}
      label={label}
      loading={currentObjects.isLoading}
      options={options}
      onTextChange={handleTextChange}
      data-testid='objectId'
      disablePortal={false}
    />
  );
}

export default ObjectSelector;
